import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { debugLog, debugError } from '@/lib/debugUtils';
import { withSearchUsageCheck, handleUsageError } from '../utils/usageMiddleware';

/**
 * Simple webhook trigger parameters with workspace support
 */
export interface WebhookTriggerParams {
  query: string;
  language_name?: string;
  location_name?: string;
  workspace_id?: string;
  workspace_name?: string; // For auto-workspace creation
  related_search_id?: string;
}

/**
 * Simple webhook response from N8N trigger
 */
export interface WebhookTriggerResponse {
  success: boolean;
  message?: string;
  data?: any;
  error?: string;
}

/**
 * Internal payload sent to N8N webhook
 */
interface WebhookPayload extends WebhookTriggerParams {
  userId: string;
  timestamp: string;
  metadata: {
    source: 'keyword_os_frontend';
    version: '1.0.0';
    user_agent?: string;
    workspace_context?: {
      workspace_id?: string;
      workspace_name?: string;
      auto_created?: boolean; // Flag to indicate if workspace was auto-created
    };
  };
}

/**
 * Simple hook return interface
 */
export interface UseWebhookTriggerReturn {
  /**
   * Trigger a webhook with basic functionality
   */
  triggerWebhook: (params: WebhookTriggerParams) => Promise<WebhookTriggerResponse>;
  
  /**
   * Current loading state
   */
  isLoading: boolean;
  
  /**
   * Current error state (simple string)
   */
  error: string | null;
}

/**
 * Simple webhook trigger hook for keyword research
 * 
 * Features:
 * - Basic webhook triggering
 * - Enhanced error handling for n8n workflows
 * - Workspace integration for payload
 * - User authentication
 * - Basic validation
 * 
 * @returns Simple webhook trigger interface
 */
export function useWebhookTrigger(): UseWebhookTriggerReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { userId, isAuthenticated, user } = useAuth();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Validate webhook parameters
   */
  const validateParams = useCallback((params: WebhookTriggerParams): void => {
    if (!params.query?.trim()) {
      throw new Error('Search query is required');
    }

    if (params.query.length > 200) {
      throw new Error('Search query is too long (maximum 200 characters)');
    }

    // Validate workspace fields if provided
    if (params.workspace_name && params.workspace_name.length > 100) {
      throw new Error('Workspace name is too long (maximum 100 characters)');
    }

    // Validate workspace_id format if provided
    if (params.workspace_id && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(params.workspace_id)) {
      throw new Error('Invalid workspace ID format');
    }
  }, []);

  /**
   * Core webhook execution function (without usage checks)
   * This is the actual webhook logic that will be wrapped with usage middleware
   */
  const executeWebhook = useCallback(async (params: WebhookTriggerParams): Promise<WebhookTriggerResponse> => {
    // Configuration check
    const webhookUrl = import.meta.env.VITE_KEYWORD_RESEARCH_WEBHOOK;
    if (!webhookUrl) {
      const configError = 'Webhook URL not configured';
      throw new Error(configError);
    }

    // Prepare payload with workspace context
    const payload: WebhookPayload = {
      ...params,
      query: params.query.trim(),
      language_name: params.language_name || 'English',
      location_name: params.location_name || 'United States',
      userId: userId!,
      timestamp: new Date().toISOString(),
      metadata: {
        source: 'keyword_os_frontend',
        version: '1.0.0',
        user_agent: navigator.userAgent,
        workspace_context: {
          workspace_id: params.workspace_id,
          workspace_name: params.workspace_name,
          auto_created: !params.workspace_id && !!params.workspace_name,
        },
      },
    };

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    debugLog(user?.primaryEmailAddress?.emailAddress, '[useWebhookTrigger] Triggering webhook with payload:', payload);
    debugLog(user?.primaryEmailAddress?.emailAddress, '[useWebhookTrigger] CRITICAL: workspace_id being sent:', params.workspace_id);

    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: abortControllerRef.current.signal,
      });

      let responseData: WebhookTriggerResponse;

      // Enhanced error handling for n8n workflows
      if (!response.ok) {
        let errorDetails;
        try {
          errorDetails = await response.text();
        } catch {
          errorDetails = 'Unknown server error';
        }

        // Special handling for ALL n8n 500 errors for keyword research workflows
        // These workflows don't have "Respond to Webhook" nodes, so 500 indicates successful start
        if (response.status === 500) {
          debugLog(user?.primaryEmailAddress?.emailAddress, '[useWebhookTrigger] n8n keyword research workflow started successfully (500 status indicates no response node)');
          debugLog(user?.primaryEmailAddress?.emailAddress, '[useWebhookTrigger] Error details:', errorDetails);
          return {
            success: true,
            message: 'Keyword research workflow started successfully',
          };
        }

        // Handle other error types appropriately
        let userFriendlyMessage: string;
        if (response.status === 429) {
          userFriendlyMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (response.status >= 400) {
          userFriendlyMessage = 'Please check your search parameters and try again.';
        } else {
          userFriendlyMessage = 'An unexpected error occurred. Please try again.';
        }

        const httpErrorMessage = `${userFriendlyMessage} (Status: ${response.status})`;
        throw new Error(httpErrorMessage);
      }

      // Handle successful response parsing
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        const textResponse = await response.text();
        responseData = { 
          success: true, 
          message: textResponse || 'Request processing started successfully',
        };
      }

      debugLog(user?.primaryEmailAddress?.emailAddress, '[useWebhookTrigger] Webhook response:', responseData);

      return responseData;

    } catch (err: any) {
      debugError(user?.primaryEmailAddress?.emailAddress, '[useWebhookTrigger] Webhook error:', err);

      // CRITICAL FIX: Handle 500 errors that are thrown as exceptions
      // Check if the error message contains status 500 or server error indicators
      const currentErrorMessage = err.message || '';
      const isServerError = currentErrorMessage.includes('500') || 
                           currentErrorMessage.includes('Internal Server Error') ||
                           currentErrorMessage.includes('servers are experiencing issues');

      if (isServerError) {
        debugLog(user?.primaryEmailAddress?.emailAddress, '[useWebhookTrigger] Detected 500 error in catch block - treating as successful workflow start');
        debugLog(user?.primaryEmailAddress?.emailAddress, '[useWebhookTrigger] Original error:', currentErrorMessage);
        
        // For keyword research, 500 errors mean the workflow started successfully
        // but couldn't respond because there's no "Respond to Webhook" node
        return {
          success: true,
          message: 'Keyword research workflow started successfully',
        };
      }

      if (err.name === 'AbortError') {
        const timeoutError = 'Request timed out - your search is still processing';
        setError(timeoutError);
        throw new Error(timeoutError);
      }

      if (err instanceof TypeError && err.message.includes('fetch')) {
        const networkError = 'Network connection failed';
        setError(networkError);
        throw new Error(networkError);
      }

      // Generic error fallback
      const fallbackErrorMessage = err.message || 'An unexpected error occurred';
      throw new Error(fallbackErrorMessage);
    }
  }, [userId, validateParams]);

  /**
   * Main webhook trigger function with usage checks and enhanced error handling
   */
  const triggerWebhook = useCallback(async (params: WebhookTriggerParams): Promise<WebhookTriggerResponse> => {
    // Authentication check
    if (!isAuthenticated || !userId) {
      const authError = 'User authentication required';
      setError(authError);
      throw new Error(authError);
    }

    // Parameter validation
    try {
      validateParams(params);
    } catch (validationError) {
      const validationErrorMessage = (validationError as Error).message;
      setError(validationErrorMessage);
      throw validationError;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use usage middleware to check and consume usage before executing search
      const { result, usageType } = await withSearchUsageCheck(
        userId,
        () => executeWebhook(params),
        params.workspace_id,
        params.query
      );

      debugLog(user?.primaryEmailAddress?.emailAddress, `[useWebhookTrigger] Search completed using ${usageType} usage`);
      
      return result;

    } catch (error: any) {
      // Handle usage-related errors with user-friendly messages
      const usageErrorInfo = handleUsageError(error);
      
      if (usageErrorInfo.isUsageError) {
        setError(usageErrorInfo.userMessage);
        throw new Error(usageErrorInfo.userMessage);
      }

      // Handle other errors
      const errorMessage = error?.message || 'An unexpected error occurred';
      setError(errorMessage);
      throw error;

    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [userId, isAuthenticated, validateParams, executeWebhook, user?.primaryEmailAddress?.emailAddress]);

  return {
    triggerWebhook,
    isLoading,
    error,
  };
}
